import { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import { IObtSurveySearchCriteriaInput, IObtSurveySearchCriteriaSorterInput } from '../../__generated__/types';
import { Obt_SearchSurveyDocument } from './obtSurveyQueries.generated';

/**
 * Maps AG Grid colId → the exact SQL column alias used in the ORDER BY clause.
 *
 * SQL source (abbreviated):
 *   pc.Partner_No, pc.System_Code, pc.Company_Code, pc.Partner_Name,
 *   ac.Country_Code, p.Master_Partner_No, pc.remark, pc.Category,
 *   pc.apar_partner_link,
 *   coalesce(ss.Survey_Status, …)  AS Survey_Status,
 *   s.Survey_id, st.Survey_Type,
 *   s.Insert_Time                  AS Survey_Request,   ← insertTime (Survey Creation Date)
 *   p.Insert_Time                                       ← masterDataInsertTime
 */
const SORT_FIELD_MAP: Record<string, string> = {
    partnerNo: 'partnerno',
    systemCode: 'sysid',
    masterPartnerNo: 'masterpartnerno',
    partnerName: 'partnername',
    category: 'category',
    companyCode: 'companycode',
    countryCode: 'countrycode',
    apArPartnerLink: 'apar_partner_link',
    surveyStatus: 'survey_status',
    surveyId: 'survey_id',
    surveyType: 'surveytype',
    surveyRequest: 'survey_startdate',
    surveyStartDate: 'survey_startdate',
    insertTime: 'inserttime',
    remark: 'remark',
};

type ObtGridColumnFilterInput = {
    field: string;
    dataType?: string;
    operator?: string;
    value?: string;
    valueTo?: string;
    values?: string[];
    secondOperator?: string;
    secondValue?: string;
    secondValueTo?: string;
    conditionJoin?: string;
};

type ObtSearchCriteriaRequest = IObtSurveySearchCriteriaInput & {
    columnFilters?: ObtGridColumnFilterInput[];
};

type NormalizedGridFilterCondition = {
    dataType?: string;
    operator?: string;
    value?: string;
    valueTo?: string;
};

const toTrimmedString = (value: unknown): string | undefined => {
    if (value == null) {
        return undefined;
    }
    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : undefined;
};

const normalizeGridFilterType = (value: unknown): string | undefined => {
    const normalized = toTrimmedString(value)?.toUpperCase();
    if (!normalized) {
        return undefined;
    }
    if (normalized === 'TEXT' || normalized === 'NUMBER' || normalized === 'DATE' || normalized === 'SET') {
        return normalized;
    }
    return undefined;
};

const normalizeStringArray = (values: unknown): string[] => {
    if (!Array.isArray(values)) {
        return [];
    }
    return values
        .map((value) => toTrimmedString(value))
        .filter((value): value is string => !!value);
};

const buildGridFilterCondition = (
    entry: any,
    fallbackFilterType?: string,
): NormalizedGridFilterCondition | undefined => {
    if (!entry) {
        return undefined;
    }
    const dataType = normalizeGridFilterType(entry.filterType) ?? fallbackFilterType;
    const operator = toTrimmedString(entry.type);
    const value = toTrimmedString(entry.filter ?? entry.dateFrom);
    const valueTo = toTrimmedString(entry.filterTo ?? entry.dateTo);
    if (!operator && !value && !valueTo) {
        return undefined;
    }
    return { dataType, operator, value, valueTo };
};

const buildSingleColumnFilter = (colId: string, entry: any): ObtGridColumnFilterInput | undefined => {
    if (!entry) {
        return undefined;
    }

    const field = toTrimmedString(colId);
    if (!field) {
        return undefined;
    }

    const filterType = normalizeGridFilterType(entry.filterType);
    if (filterType === 'SET') {
        const values = normalizeStringArray(entry.values);
        if (values.length === 0) {
            return undefined;
        }
        return {
            field,
            dataType: 'SET',
            values,
        };
    }

    const primary = buildGridFilterCondition(entry.condition1 ?? entry, filterType);
    if (!primary) {
        return undefined;
    }
    const secondary = buildGridFilterCondition(entry.condition2, filterType);

    return {
        field,
        dataType: primary.dataType,
        operator: primary.operator,
        value: primary.value,
        valueTo: primary.valueTo,
        secondOperator: secondary?.operator,
        secondValue: secondary?.value,
        secondValueTo: secondary?.valueTo,
        conditionJoin: toTrimmedString(entry.operator)?.toUpperCase(),
    };
};

const buildColumnFilters = (filterModel: Record<string, any>): ObtGridColumnFilterInput[] => {
    const filters: ObtGridColumnFilterInput[] = [];
    Object.entries(filterModel).forEach(([colId, entry]) => {
        const mappedFilter = buildSingleColumnFilter(colId, entry);
        if (mappedFilter) {
            filters.push(mappedFilter);
        }
    });
    return filters;
};

export default class ObtPartnerListDatasource implements IServerSideDatasource {
    private readonly client: any;
    private readonly filters: IObtSurveySearchCriteriaInput;
    private readonly cacheBlockSize: number;
    private readonly setLastSearch: (criteria: IObtSurveySearchCriteriaInput) => void;

    constructor(
        client: any,
        filters: IObtSurveySearchCriteriaInput,
        cacheBlockSize: number,
        setLastSearch: (criteria: IObtSurveySearchCriteriaInput) => void
    ) {
        this.client = client;
        this.filters = filters;
        this.cacheBlockSize = cacheBlockSize;
        this.setLastSearch = setLastSearch;
    }

    getRows(params: IServerSideGetRowsParams): void {
        const startRow = params.request.startRow ?? 0;
        const pageNo = this.cacheBlockSize > 0 ? Math.floor(startRow / this.cacheBlockSize) : 0;

        // ── Sort ──────────────────────────────────────────────────────────────
        const sorter: IObtSurveySearchCriteriaSorterInput[] = params.request.sortModel.map((s) => ({
            property:  SORT_FIELD_MAP[s.colId] ?? s.colId,
            direction: s.sort.toUpperCase(),
        }));

        // ── Column filters (additional grid constraints) ───────────────────────
        const columnFilters = buildColumnFilters(params.request.filterModel ?? {});

        // ── Final criteria ────────────────────────────────────────────────────
        const criteria: ObtSearchCriteriaRequest = {
            ...this.filters,
            pageNo,
            pageSize: this.cacheBlockSize,
            ...(sorter.length > 0 ? { sorters: sorter } : {}),
            ...(columnFilters.length > 0 ? { columnFilters } : {}),
        };

        this.setLastSearch(criteria);

        this.client
            .query({
                query: Obt_SearchSurveyDocument,
                variables: { criteria },
                fetchPolicy: 'network-only',
            })
            .then((response: any) => {
                const result = response?.data?.obtSearchSurvey;
                params.success({ rowData: result?.surveyList ?? [], rowCount: result?.total ?? 0 });
            })
            .catch(() => {
                params.fail();
            });
    }
}
